"use client"

import { ampli } from "@/ampli"
import { toast } from "@/components/Toast"
import { Avatar, Text } from "@/elements"
import { InputLabel } from "@/elements/InputLabel"
import { TextArea } from "@/elements/TextArea"
import { FragmentType, graphql, useFragment } from "@/gql"
import { GetJobTitleDocument } from "@/gql/graphql"
import { useSession } from "@/hooks/useSession"
import { useMutation, useQuery } from "@apollo/client"
import clsx from "clsx"
import { ChangeEvent, useCallback, useMemo, useState } from "react"
import { ControlledModal } from "../Dialog"
import { Dropdown } from "../Dropdown"
import { decodeGraphqlNodeId } from "@/tracking"

graphql(`
  query GetJobTitle($id: ID!) {
    node(id: $id) {
      __typename
      id
      ... on JobPosting {
        title
      }
    }
  }
`)

const GetMyJobPostingsQuery = graphql(`
  query GetMyJobPostings($first: Int, $after: String, $title: String, $clientMode: Boolean! = false) {
    currentUser {
      id
      __typename
      creative @skip(if: $clientMode) {
        jobPostings(status: PUBLISHED, first: $first, after: $after, title: $title) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              __typename
              id
              title
              publishedAtFormatted(format: "MMMM D")
            }
          }
        }
      }
      client @include(if: $clientMode) {
        jobPostings(status: PUBLISHED, first: $first, after: $after, title: $title) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            cursor
            node {
              __typename
              id
              title
              publishedAtFormatted(format: "MMMM D")
            }
          }
        }
      }
    }
  }
`)

export const InviteToJobMutation = graphql(`
  mutation InviteToJob($input: InviteToJobInput!) {
    inviteToJob(input: $input) {
      errors {
        field
        messages
      }
      jobInvitation {
        id
      }
    }
  }
`)

const InviteToAJobDialogFragment = graphql(`
  fragment InviteToAJobDialog on IndividualCreative {
    id
    __typename
    avatar
    avatarInitials
    user {
      __typename
      id
      name
    }
  }
`)

interface Props {
  trigger?: React.ReactElement<any>
  creative: FragmentType<typeof InviteToAJobDialogFragment> | null | undefined
  modalOpen?: boolean
  onModalClose?: () => void
  jobId?: string
  triggerWrapperClassName?: string
  trackingEntryPoint: string
}

const PAGE_SIZE = 6

export const InviteToAJobDialog: React.FC<Props> = props => {
  const creative = useFragment(InviteToAJobDialogFragment, props.creative)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [noJobError, setNoJobError] = useState(false)
  const { data: session } = useSession()
  const { onModalClose, trigger, jobId, triggerWrapperClassName } = props
  const [uncontrolledModalOpen, setUncontrolledModalOpen] = useState(false)
  const modalOpen = props.modalOpen || uncontrolledModalOpen

  const userIsClient = session?.account === "client"

  const handleModalClose = useCallback(() => {
    setUncontrolledModalOpen(false)
    onModalClose?.()
    setSelectedJob(undefined)
    setMessageValue("")
    setNoJobError(false)
  }, [onModalClose])

  const handleOpenChange = (open: boolean) => {
    if (open) {
      ampli.inviteToJobStart({
        account_type: session?.account || "",
        entry_point: props.trackingEntryPoint,
      })
      setUncontrolledModalOpen(open)
    } else {
      handleModalClose()
    }
  }
  const {
    data,
    loading: loadingJobs,
    networkStatus,
    fetchMore,
  } = useQuery(GetMyJobPostingsQuery, {
    variables: { clientMode: userIsClient, title: "", first: PAGE_SIZE },
    skip: !modalOpen,
  })
  const { data: dataJobTitle } = useQuery(GetJobTitleDocument, {
    variables: { id: `${jobId}` },
    skip: !jobId,
  })
  const [inviteToJob, { loading }] = useMutation(InviteToJobMutation)

  const pageInfo = userIsClient
    ? data?.currentUser?.client?.jobPostings?.pageInfo
    : data?.currentUser?.creative?.jobPostings?.pageInfo

  const [selectedJob, setSelectedJob] = useState<{ name: string; id: string } | undefined>(undefined)
  const [messageValue, setMessageValue] = useState<string>("")

  const handleChange = (item: { name: string; id: string }) => {
    setSelectedJob(item)
  }

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessageValue(e.currentTarget.value)
  }

  const handleSubmit = async () => {
    if (!isSubmitting) {
      setIsSubmitting(true)
      const jobPostingIdUnion = jobId || selectedJob?.id
      if (!jobPostingIdUnion) {
        setNoJobError(true)
        setIsSubmitting(false)
        return
      }
      if (creative?.id) {
        const res = await inviteToJob({
          variables: { input: { creative: creative?.id, message: messageValue, jobPosting: jobPostingIdUnion } },
        })
        if (res.data?.inviteToJob?.jobInvitation?.id) {
          toast.success("Invitation sent")
          ampli.inviteToJobComplete({
            job_id: decodeGraphqlNodeId(jobId).numericalId || "",
            entry_point: props.trackingEntryPoint,
            job_type: dataJobTitle?.node?.__typename as string,
          })
          handleModalClose()
        }
      }
      setIsSubmitting(false)
    }
  }

  const selectOptions = useMemo(() => {
    if (userIsClient) {
      return (
        data?.currentUser?.client?.jobPostings?.edges.map(job => ({
          id: job?.node?.id!,
          name: `${job?.node?.title} - Created ${job?.node?.publishedAtFormatted}`,
        })) ?? []
      )
    }

    return (
      data?.currentUser?.creative?.jobPostings?.edges.map(job => ({
        id: job?.node?.id!,
        name: `${job?.node?.title} - Created ${job?.node?.publishedAtFormatted}`,
      })) ?? []
    )
  }, [data, userIsClient])

  return (
    <>
      <div
        onClick={() => setUncontrolledModalOpen(true)}
        className={clsx("flex flex-shrink-0", triggerWrapperClassName)}>
        {trigger}
      </div>

      <ControlledModal
        open={modalOpen}
        onOpenChange={handleOpenChange}
        header={{ title: "", fixed: true, variant: "acknowledgement" }}
        body={
          <div className="">
            <div className="flex flex-row items-center gap-x-3">
              <Avatar size="xl" src={creative?.avatar} initials={creative?.avatarInitials} />
              <Text variant="h5">
                Invite {creative?.user.name} to{" "}
                {dataJobTitle?.node?.__typename === "ClientJobPosting" ? dataJobTitle.node.title : "a job"}
              </Text>
            </div>
            {!jobId ? (
              <>
                <div className="mt-10 flex flex-row gap-x-2">
                  <div className="flex flex-1 flex-col gap-y-1">
                    <InputLabel label="Job" />
                    <Dropdown
                      onSelect={handleChange}
                      value={selectedJob?.name || ""}
                      networkStatus={networkStatus}
                      options={selectOptions}
                      loading={loadingJobs}
                      placeholder="Select a job"
                      pageSize={PAGE_SIZE}
                      fetchMore={fetchMore}
                      pageInfo={pageInfo}
                    />
                    <div className="h-[16px]">
                      {noJobError && (
                        <Text variant="caption" className="text-red-default">
                          Please select a job
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            <div className="mb-8 mt-4 flex flex-1 flex-col gap-y-1">
              <InputLabel label="Message" />
              <TextArea
                value={messageValue}
                onChange={handleMessageChange}
                placeholder="Add message here"
                className="h-[220px] w-full px-3 py-2"
              />
            </div>
          </div>
        }
        footer={{
          primaryButton: {
            children: "Send invitation",
            onClick: handleSubmit,
            loading: loading,
          },
          fixed: true,
        }}
      />
    </>
  )
}
